import { useReducer, useRef, KeyboardEvent, ChangeEvent, useMemo, useCallback } from 'react';
import BaseButton from '@/components/ui/BaseButton';
import BaseDataTable from '@/components/ui/BaseDataTable';
import BaseDialog from '@/components/ui/BaseDialog';
import BaseSelect from '@/components/form/BaseSelect';
import BaseTextField from '@/components/form/BaseTextField';
import SearchBar from '@/components/form/SearchBar';
import {
    AddTagItem,
    RegistrationAddTagDialogAction,
    RegistrationAddTagDialogState,
} from '@/types/pages/storeManagement/registration';
import { Box, FormControl, FormHelperText, styled, Typography } from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid';
import { Add, Delete, Upload } from '@mui/icons-material';
import { decodeMac, encodedMACFormat } from '@/utils/inputUtils';
import '@/styles/pages/StoreMgmt/registration/AddTagDialog.scss';
import { chain, toNumber } from 'lodash';
import { useAppContext } from '@/provider/contexts';
import { useRegistrationState } from '@/provider/registration/registrationContext';
import { addToWhiteListAPI, registerTagAPI } from '@/api/storeManagement/registration';
import { useTranslation } from 'react-i18next';
import { useRegistrationLifecycleState } from '@/provider/registration/registrationLifecycleContext';

const tagReducer = (state: RegistrationAddTagDialogState, action: RegistrationAddTagDialogAction) => {
    switch (action.type) {
        case 'SET_SEARCH_MAC':
            return { ...state, searchMac: action.payload };
        case 'SET_INPUT_MAC':
            return { ...state, inputMAC: action.payload };
        case 'SET_NEWEST_INPUT_MAC':
            return { ...state, newestInputMac: action.payload };
        case 'SET_INPUT_DEVICE':
            return { ...state, inputDevice: action.payload };
        case 'SET_KEYWORD':
            return { ...state, keyword: action.payload };
        case 'SET_ORIGINAL_LIST':
            return { ...state, originalList: action.payload, list: action.payload };
        case 'SET_IMPORT_LIST': {
            const existing = new Set(state.originalList.map((x) => x.mac));

            const next: AddTagItem[] = [];
            for (const item of action.payload) {
                if (existing.has(item.mac)) continue;
                existing.add(item.mac);
                next.push(item);
            }

            return {
                ...state,
                originalList: [...state.originalList, ...next],
                list: [...state.originalList, ...next],
            };
        }
        case 'SET_VALIDATED_MESSAGE':
            return { ...state, validatedMessage: action.payload };
        case 'SET_SELECTED_IDS':
            return { ...state, selectedIds: action.payload };
        case 'SET_CONFIRMED_TAG':
            return { ...state, confirmedTagList: action.payload };
        case 'TOGGLE_CONFIRMED_TAG':
            return { ...state, toggleConfirmedTag: action.payload };
        case 'CLEAR_DIALOG_DATA':
            return defaultState;
        default:
            return state;
    }
};

const defaultState: RegistrationAddTagDialogState = {
    searchMac: '',
    inputMAC: '',
    newestInputMac: '',
    inputDevice: 'keyboard',
    keyword: '',
    originalList: [] as AddTagItem[],
    list: [] as AddTagItem[],
    selectedIds: [],
    validatedMessage: '',
    confirmedTagList: [],
    toggleConfirmedTag: false,
};

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});
const MAC_DEFAULT_LENGTH = 12;

const AddTagDialog = () => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const { refreshTable } = useRegistrationLifecycleState();
    const { state, dispatch } = useRegistrationState();
    const [
        {
            searchMac,
            inputMAC,
            newestInputMac,
            inputDevice,
            originalList,
            list,
            selectedIds,
            validatedMessage,
            confirmedTagList,
            toggleConfirmedTag,
        },
        tagDispatch,
    ] = useReducer(tagReducer, defaultState);
    const inputRef = useRef<HTMLInputElement>(null);
    const isQRCode = useRef(false);
    const apiRef = useGridApiRef();

    const columns: GridColDef[] = [
        { field: 'mac', headerName: t('MAC'), flex: 1 },
        { field: 'product', headerName: t('Product'), flex: 1 },
    ];

    const inputDeviceOptions = useMemo(
        () =>
            [
                { label: t('Keyboard'), value: 'keyboard' },
                { label: t('QR code'), value: 'qrCode' },
                { label: t('CSV'), value: 'csv' },
            ] as { label: string; value: string }[],
        [t],
    );

    const filterList = useMemo(
        () => originalList.filter((o) => o.mac.includes(searchMac) || o.product?.includes(searchMac)),
        [originalList, searchMac],
    );

    const MACInputValidate = () => {
        if (isQRCode.current) {
            tagDispatch({ type: 'SET_VALIDATED_MESSAGE', payload: '' });
            return true;
        }
        if (inputMAC.length !== 12) {
            const warnText = inputDevice === 'keyboard' ? t('MAC must be 12 characters') : t('Invalid QR code data');
            inputRef.current?.select();
            tagDispatch({ type: 'SET_VALIDATED_MESSAGE', payload: warnText });
            return false;
        }
        tagDispatch({ type: 'SET_VALIDATED_MESSAGE', payload: '' });
        return true;
    };

    const macInputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
        let inputMac = e.target.value;
        if (encodedMACFormat(e.target.value)) {
            inputMac = decodeMac(inputMac.substring(2));
        }
        tagDispatch({ type: 'SET_INPUT_MAC', payload: inputMac });
    };
    const keyUpHandler = (e: KeyboardEvent<Element>) => {
        if (e.key === 'Enter') {
            tagDispatch({ type: 'SET_VALIDATED_MESSAGE', payload: '' });
            isQRCode.current = isQRCodeInput(inputMAC);
            appendListHandler();
        }
    };
    const isQRCodeInput = (input: string) => {
        return /^qrc:\d+,type:[0-9A-F]+,list:[0-9A-F]+$/i.test(input);
    };
    const appendListHandler = () => {
        if (!MACInputValidate()) return;
        appendList();
        tagDispatch({ type: 'SET_INPUT_MAC', payload: '' });
    };

    const validateMac = (mac: string): boolean => {
        const validation = /^[0-9A-F]+$/;
        mac = mac.trim().toUpperCase();
        return mac.length === 12 && validation.test(mac);
    };

    const importHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const file = input.files?.[0];

        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (_event) => {
            try {
                const importData = _event.target?.result;

                if (!importData) return;
                const data = (importData as string).split(/\r?\n/);
                const newList = data
                    .map((item) => {
                        const parts = item.split(',');
                        const MAC = parts[0];
                        const productCode = parts[1];
                        return { id: MAC, mac: MAC, product: productCode };
                    })
                    .filter((o) => validateMac(o.mac));

                tagDispatch({ type: 'SET_IMPORT_LIST', payload: newList });
            } catch (error: unknown) {
                if (error instanceof Error) {
                    setNotification({ message: error.message || t('File import failed.'), severity: 'error' });
                    return;
                }
                console.error('Error Import Files', error);
            }
        };

        reader.onerror = () => {
            setNotification({ message: t('File import failed.'), severity: 'error' });
        };

        reader.readAsText(file);
    };

    const appendList = () => {
        if (list.some((item) => item.mac === inputMAC)) return;
        if (isQRCode.current) {
            const parts = inputMAC.split(',');

            // Extract the total
            const total = toNumber(parts[0].split(':')[1]);
            // Extract the type
            const type = parts[1].split(':')[1];

            // Extract the list string
            const listString = parts[2].split(':')[1];

            // Split the list by type length into subMACs
            const chunk = MAC_DEFAULT_LENGTH - type.length;
            const regex = new RegExp(`.{1,${chunk}}`, 'g');
            const subMACs = listString.match(regex) || [];

            // Combine type with each subMACs
            const validList = chain(subMACs)
                .map((subMAC) => {
                    const mac = type + subMAC;
                    return {
                        id: mac,
                        mac,
                        product: null,
                    };
                })
                .filter((item) => item.mac.length === 12)
                .value();

            const invalid = validList.length !== total;
            if (invalid) {
                tagDispatch({
                    type: 'SET_VALIDATED_MESSAGE',
                    payload: t('Warning QR code: Found only {{found}} devices from {{total}} devices', {
                        found: validList.length,
                        total,
                    }),
                });
                return;
            }

            // now remove any duplicates
            const uniqueNewList = validList.filter(
                (item) => !list.some((existingItem) => existingItem.mac === item.mac),
            );
            tagDispatch({ type: 'SET_ORIGINAL_LIST', payload: [...list, ...uniqueNewList] });
        } else {
            const newItem = { id: inputMAC, mac: inputMAC, product: null };
            const newList = [...list, newItem];
            tagDispatch({ type: 'SET_ORIGINAL_LIST', payload: newList });
            tagDispatch({ type: 'SET_NEWEST_INPUT_MAC', payload: inputMAC });
        }
    };

    const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value || '';
        tagDispatch({ type: 'SET_SEARCH_MAC', payload: value.toLocaleUpperCase() });
    };

    const deleteHandler = () => {
        const newList = list.filter((item) => !selectedIds.includes(item.id));
        tagDispatch({ type: 'SET_ORIGINAL_LIST', payload: newList });
    };

    const handleClose = useCallback(
        () => dispatch({ type: 'TOGGLE_DIALOG', payload: { dialog: 'isTagOpen', isOpen: false } }),
        [],
    );

    const handleConfirm = async () => {
        dispatch({ type: 'TOGGLE_DIALOG', payload: { dialog: 'isTagOpen', isOpen: false } });
        dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'deviceTableLoading', isLoading: true } });
        dispatch({ type: 'SET_TABLE_STATE', payload: { status: 'loading' } });
        const macList = list.map((item) => item.mac);
        if (!macList.length) return;
        try {
            const {
                data: { isSuccess, message, data },
            } = await registerTagAPI({
                store_id: manageStoreId as number,
                mac_list: macList,
            });

            if (isSuccess) {
                const { confirmed, store_settings } = data || {};
                if (store_settings.asset_list_feature === '1') {
                    await addToWhiteListHandler(macList);
                }
                if (confirmed.length > 0) {
                    tagDispatch({ type: 'TOGGLE_CONFIRMED_TAG', payload: true });
                    tagDispatch({ type: 'SET_CONFIRMED_TAG', payload: data.confirmed });
                }
                setNotification({ message, severity: 'success' });
                void refreshTable();
                return;
            }

            throw new Error(message);
        } catch (error: unknown) {
            dispatch({ type: 'TOGGLE_DIALOG', payload: { dialog: 'isTagOpen', isOpen: false } });
            if (error instanceof Error) {
                setNotification({ message: error?.message || t('Failed to Add Tags'), severity: 'error' });
                return;
            }
            console.error('Error saving settings:', error);
        }
    };

    const addToWhiteListHandler = async (macList: string[]) => {
        try {
            const {
                data: { isSuccess, message },
            } = await addToWhiteListAPI(manageStoreId as number, macList);
            if (isSuccess) return;
            throw new Error(message || t('Allowlist failed after tag registration'));
        } catch (error: unknown) {
            if (error instanceof Error) {
                setNotification({ message: error?.message || t('Failed to Add WhiteList'), severity: 'error' });
                return;
            }
            console.error('Error saving settings:', error);
        }
    };

    return (
        <>
            <BaseDialog
                dialogTitle={t('Add Tag')}
                maxWidth="md"
                open={state.dialogState.isTagOpen}
                confirmButtonText={t('Save')}
                cancelButtonText={t('Cancel')}
                onClose={handleClose}
                onConfirm={handleConfirm}
                TransitionProps={{
                    onEnter: () => {
                        inputRef.current?.focus();
                    },
                    onExited: () => tagDispatch({ type: 'CLEAR_DIALOG_DATA' }),
                }}
            >
                <Box sx={{ py: 1, width: 800 }}>
                    <Box sx={{ width: '100%', display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 2 }}>
                            {inputDevice === 'csv' ? (
                                <BaseButton
                                    component="label"
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Upload />}
                                >
                                    {t('Upload')}
                                    <VisuallyHiddenInput
                                        type="file"
                                        accept=".csv"
                                        onClick={(_event) => {
                                            _event.currentTarget.value = '';
                                        }}
                                        onChange={importHandler}
                                    />
                                </BaseButton>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: `${validatedMessage !== '' ? 'start' : 'center'}`,
                                        gap: 1,
                                    }}
                                >
                                    <FormControl error={validatedMessage !== ''} fullWidth>
                                        <BaseTextField
                                            inputRef={inputRef}
                                            label={t('MAC Address')}
                                            value={inputMAC}
                                            size="small"
                                            fullWidth
                                            error={validatedMessage !== ''}
                                            onChange={macInputChangeHandler}
                                            onKeyUp={(e) => keyUpHandler(e)}
                                        />
                                        <FormHelperText>{validatedMessage}</FormHelperText>
                                    </FormControl>

                                    <BaseButton startIcon={<Add />} onClick={appendListHandler}>
                                        {t('Append')}
                                    </BaseButton>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <BaseSelect
                                label={t('Input device')}
                                size="small"
                                options={inputDeviceOptions}
                                value={inputDevice}
                                onChange={(e) =>
                                    tagDispatch({ type: 'SET_INPUT_DEVICE', payload: e.target.value as string })
                                }
                            />
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <SearchBar onChange={handleOnChange} size="small" sx={{ my: 1.5 }} />
                            <BaseButton startIcon={<Delete />} onClick={deleteHandler}>
                                {t('Delete')}
                            </BaseButton>
                        </Box>
                    </Box>
                    <Box>
                        <BaseDataTable
                            apiRef={apiRef}
                            density="compact"
                            columns={columns}
                            rows={filterList}
                            checkboxSelection
                            getRowClassName={(params) => {
                                return params.id === newestInputMac ? 'highlight-new-row' : '';
                            }}
                            onRowSelectionModelChange={(ids) =>
                                tagDispatch({ type: 'SET_SELECTED_IDS', payload: ids as string[] })
                            }
                        />
                    </Box>
                </Box>
            </BaseDialog>

            <BaseDialog
                dialogTitle={t('Registered Tags Detected')}
                open={toggleConfirmedTag}
                confirmButtonText={t('OK')}
                cancelButtonText=""
                onConfirm={() => tagDispatch({ type: 'TOGGLE_CONFIRMED_TAG', payload: false })}
            >
                <Box>
                    <Typography sx={{ mb: 1 }}>{t('These tags are already registered:')}</Typography>
                    <Box sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                        {confirmedTagList.slice(0, 10).map((mac) => `- ${mac}\n`)}

                        {confirmedTagList.length > 10 && t('...and many more')}
                    </Box>
                </Box>
            </BaseDialog>
        </>
    );
};

export default AddTagDialog;
